// EVERY PERSON ALREADY NAMED ON THIS CLIENT, in one list.
//
// A single director routinely has to be typed four times on one client form:
// once on the board, once as a signatory, once as a statutory officer, once as
// a shareholder — with their PAN and DIN retyped each time. That is where the
// mistakes come from, and it is also just tedious: the second time somebody
// types a 10-character PAN they get it wrong, and now the same human exists
// twice under two PANs and nothing in the system can tell.
//
// So the form gathers whoever is already on the record and offers them back.
// Nothing here writes; it only answers "who do we already know about, and what
// do we know about them".

// Identity is PAN first (it IS the tax identity), then DIN, then a normalised
// name. Name alone is a weak key — two Sharmas are ordinary — so it is only
// used when nothing stronger exists.
const normName = (v) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');
const keyFor = (p) => {
  const pan = String(p.pan || '').trim().toUpperCase();
  if (pan) return `pan:${pan}`;
  const din = String(p.din || '').trim();
  if (din) return `din:${din}`;
  const name = normName(p.name);
  return name ? `name:${name}` : '';
};

// Where each list's identity fields actually live. Kept as data rather than as
// six near-identical loops, so adding a people-bearing list later is one entry.
const SOURCES = [
  { list: 'contacts', label: 'Contact', role: (r) => r.role },
  { list: 'signatories', label: 'Signatory', role: (r) => r.role },
  { list: 'board', label: 'Director', role: (r) => r.role },
  { list: 'statutoryOfficers', label: 'Officer', role: (r) => r.officerType },
  { list: 'owners', label: 'Shareholder', role: () => '' },
  { list: 'employees', label: 'Employee', role: (r) => r.designation },
];

/**
 * Merge every person on the client into one de-duplicated list.
 *
 * Later mentions FILL BLANKS but never overwrite: the first list that supplied
 * a PAN keeps it, so a picker can't quietly replace a value somebody checked
 * with one somebody guessed. Each person carries `seenIn` — the lists they
 * already appear on — because "Ravi Sharma (Director, Signatory)" is what makes
 * the option recognisable in a dropdown.
 *
 * @param {object} sources — { contacts, signatories, board, statutoryOfficers, owners, employees }
 */
export function collectClientPeople(sources = {}) {
  const byKey = new Map();

  for (const src of SOURCES) {
    for (const row of sources[src.list] || []) {
      // An owner may be a company, not a person — a corporate shareholder has
      // no PAN-holder identity to reuse on a board seat.
      if (src.list === 'owners' && row.holderType && row.holderType !== 'individual') continue;
      const key = keyFor(row);
      if (!key) continue;

      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          name: String(row.name || '').trim(),
          pan: String(row.pan || '').trim().toUpperCase(),
          din: String(row.din || '').trim(),
          // Carried for the same reason as the PAN: a signatory, a director
          // and a shareholder can all be the same human, and an Aadhaar
          // retyped three times is an Aadhaar wrong once.
          aadhaar: String(row.aadhaar || '').trim(),
          email: String(row.email || '').trim(),
          phone: String(row.phone || '').trim(),
          designation: String(src.role(row) || '').trim(),
          seenIn: [],
        });
      }
      const person = byKey.get(key);
      // Fill blanks only.
      for (const f of ['name', 'pan', 'din', 'aadhaar', 'email', 'phone', 'designation']) {
        const incoming = f === 'designation' ? src.role(row) : row[f];
        if (!person[f] && incoming) person[f] = String(incoming).trim();
      }
      if (!person.seenIn.includes(src.label)) person.seenIn.push(src.label);
    }
  }

  return Array.from(byKey.values())
    .filter((p) => p.name)
    .sort((a, b) => a.name.localeCompare(b.name));
}

// "Ravi Sharma — Director, Signatory". What the dropdown shows.
export const describePerson = (p) =>
  (p.seenIn.length ? `${p.name} — ${p.seenIn.join(', ')}` : p.name);

// A GSTIN carries its holder's PAN in characters 3-12. So a client with any
// GSTIN already told us their PAN, and asking for it again is asking for a
// value we can derive and they can mistype.
export function panFromGstins(gstins = []) {
  for (const g of gstins) {
    const raw = String(g?.gstin || '').trim().toUpperCase();
    if (raw.length >= 12) {
      const pan = raw.slice(2, 12);
      if (/^[A-Z]{5}\d{4}[A-Z]$/.test(pan)) return pan;
    }
  }
  return '';
}
